import type { MissingCrateItem } from '@/entities/order/model'
import type { CrateType } from '@/features/order/model/orderDetails.types'
import { Button, Drawer, Modal, Radio, Stack, Text, Textarea } from '@mantine/core'
import { IconCircleCheck } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { useMissingCrateReasons } from '@/entities/state/model'
import { useResponsiveModal } from '@/shared/lib/mantine'
import drawerClasses from '../status-drawer/StatusUpdateDrawer.module.css'

interface MissingCratesModalProps {
  opened: boolean
  onClose: () => void
  crate: CrateType
  orderId: string
  onMissingCrateMarked: (item: MissingCrateItem) => void
  submittedCrate?: MissingCrateItem
  /** BD-8595: force sheet content for the onboarding tutorial. */
  tutorialPreset?: {
    reason: string
    succeeded: boolean
  }
}

export function MissingCratesModal({ opened, onClose, crate, orderId, onMissingCrateMarked, submittedCrate, tutorialPreset }: MissingCratesModalProps) {
  const reasons = useMissingCrateReasons()
  const { isSmUp, transitionProps } = useResponsiveModal()
  const isReadOnly = !!submittedCrate

  const [selectedReason, setSelectedReason] = useState('')
  const [comment, setComment] = useState('')
  const [succeeded, setSucceeded] = useState(false)

  /* eslint-disable react-hooks-extra/no-direct-set-state-in-use-effect -- reset local drawer state when modal closes */
  useEffect(() => {
    if (!opened) {
      setSelectedReason('')
      setComment('')
      setSucceeded(false)
      return
    }
    if (tutorialPreset) {
      setSelectedReason(tutorialPreset.reason)
      setSucceeded(tutorialPreset.succeeded)
    }
  }, [opened, tutorialPreset])
  /* eslint-enable react-hooks-extra/no-direct-set-state-in-use-effect */

  // BD-8394: the comment is optional and the placeholder says so.
  //
  // BD-8548: the reason is not. Tempo requires a trimmed, non-empty reason and rejects
  // anything else, so submitting without one produced a local success for a record the
  // backend never kept. Letting the submission through when the store has no reasons
  // configured (BD-8394) is what made that reachable, so it is gated again: a store with
  // no enabled reasons is a configuration problem, and reporting a crate missing into
  // nowhere is worse than not offering it.
  function handleSubmit() {
    const missingCrate: MissingCrateItem = {
      barcode: crate.type,
      orderId,
      areaCode: 'N/A',
      crateNumber: 'N/A',
      crateType: crate.type,
      reason: selectedReason.trim(),
      comment: comment.trim() || undefined,
    }
    onMissingCrateMarked(missingCrate)
    setSucceeded(true)
    setTimeout(onClose, 3000)
  }

  const displayReason = isReadOnly ? submittedCrate.reason : selectedReason
  const displayComment = isReadOnly ? (submittedCrate.comment ?? '') : comment
  const showSuccess = succeeded || tutorialPreset?.succeeded === true
  const isTutorialWalkthrough = !!tutorialPreset && !tutorialPreset.succeeded

  // BD-8595 Step 9 desktop: spotlight the success panel ~1.5× taller (tip stays normal size).
  const successSpotlightTall = showSuccess && !!tutorialPreset?.succeeded && isSmUp

  const body = showSuccess
    ? (
        <Stack
          align="center"
          justify="center"
          gap="sm"
          py={successSpotlightTall ? 48 : 'md'}
          style={successSpotlightTall ? { minHeight: '10.5rem' } : undefined}
          data-onboarding="tutorial-missing-crates-success"
        >
          <IconCircleCheck size={40} color="var(--mantine-color-green-6)" />
          <Text fw={500}>Missing crates recorded.</Text>
        </Stack>
      )
    : (
        // Spotlight: reason radios + comment field + Submit (same UI as real missing-mark flow).
        <Stack
          gap="md"
          data-onboarding={tutorialPreset ? 'tutorial-missing-crates-sheet' : undefined}
        >
          <Text size="sm" c="dimmed">
            {isReadOnly
              ? 'Can\'t edit a submitted missing reason. If the crate is found, scan it to mark it complete.'
              : 'One or more crates are missing due to:'}
          </Text>

          <Radio.Group value={displayReason} onChange={isReadOnly || tutorialPreset ? undefined : setSelectedReason}>
            <Stack gap="xs">
              {reasons.map(reason => (
                <Radio
                  key={reason}
                  value={reason}
                  label={reason}
                  disabled={isReadOnly || !!tutorialPreset}
                  classNames={{ label: drawerClasses.radioOption }}
                />
              ))}
            </Stack>
          </Radio.Group>

          <Textarea
            placeholder="Tell us more about why some crates couldn't be scanned (optional)"
            value={displayComment}
            onChange={isReadOnly || isTutorialWalkthrough ? undefined : e => setComment(e.currentTarget.value)}
            disabled={isReadOnly || isTutorialWalkthrough}
            minRows={3}
            autosize
          />

          {!isReadOnly && !tutorialPreset && (
            <Button
              variant="filled"
              color="gray.8"
              fullWidth
              size="md"
              radius="md"
              disabled={!selectedReason.trim()}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          )}
          {isTutorialWalkthrough && (
            <Button
              variant="filled"
              color="gray.8"
              fullWidth
              size="md"
              radius="md"
              disabled
            >
              Submit
            </Button>
          )}
        </Stack>
      )

  // BD-8595 Steps 8–9: spotlight the whole Missing Crates chrome (not only the inner form).
  const tutorialModalOnboarding = tutorialPreset ? 'tutorial-missing-crates-modal' : undefined

  // Desktop: centered modal over Order Details (same pattern as InitialsCaptureModal).
  if (isSmUp) {
    return (
      <Modal
        opened={opened}
        onClose={onClose}
        title="Missing Crates"
        centered
        radius="md"
        // BD-8595 Step 8 desktop: ~1.5× Mantine `md` (440 → 660).
        size={660}
        withCloseButton
        transitionProps={transitionProps}
        data-onboarding={tutorialModalOnboarding}
        // Above Driver.js overlay (10000) so Steps 8–9 sit in the spotlight, not under the mask.
        zIndex={tutorialPreset ? 10002 : undefined}
        classNames={{
          header: drawerClasses.drawerHeader,
          title: drawerClasses.drawerTitle,
          close: drawerClasses.drawerClose,
          body: drawerClasses.drawerBody,
        }}
      >
        {body}
      </Modal>
    )
  }

  // Mobile: bottom sheet over the order details screen.
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="bottom"
      title="Missing Crates"
      radius="md"
      withCloseButton
      transitionProps={transitionProps}
      data-onboarding={tutorialModalOnboarding}
      // Above Driver.js overlay (10000) so Steps 8–9 sit in the spotlight, not under the mask.
      zIndex={tutorialPreset ? 10002 : undefined}
      classNames={{
        header: drawerClasses.drawerHeader,
        title: drawerClasses.drawerTitle,
        close: drawerClasses.drawerClose,
        content: drawerClasses.drawerContent,
        body: drawerClasses.drawerBody,
      }}
    >
      {body}
    </Drawer>
  )
}
